import * as E from "fp-ts/Either"
import axios from "axios"
import { BehaviorSubject, Subject } from "rxjs"
import { Ref, ref, watch } from "vue"

import { getService } from "@hoppscotch/common/modules/dioc"
import {
  AuthEvent,
  AuthPlatformDef,
  HoppUser,
} from "@hoppscotch/common/platform/auth"
import { PersistenceService } from "@hoppscotch/common/services/persistence"
import { WorkspaceService } from "@hoppscotch/common/services/workspace.service"
import { TeamAccessRole } from "@hoppscotch/common/helpers/backend/graphql"
import { runGQLQuery } from "@hoppscotch/common/helpers/backend/GQLClient"
import {
  GetTeamDocument,
  GetTeamQuery,
  GetTeamQueryVariables,
} from "@hoppscotch/common/helpers/backend/graphql"

import { getAllowedAuthProviders, updateUserDisplayName } from "./api"

// 扩展 AuthEvent 类型以包含 token_refresh 事件
type ExtendedAuthEvent = AuthEvent | { event: "token_refresh" }

export const authEvents$ = new Subject<ExtendedAuthEvent>()
const currentUser$ = new BehaviorSubject<HoppUser | null>(null)
export const probableUser$ = new BehaviorSubject<HoppUser | null>(null)

const persistenceService = getService(PersistenceService)

axios.interceptors.request.use(async (config) => {
  // 推荐同步读取 localStorage，或用 persistenceService
  const accessToken = await persistenceService.getLocalConfig("accessToken")
  if (accessToken) {
    config.headers = config.headers || {}
    config.headers["Authorization"] = `Bearer ${accessToken}`
  }
  return config
})

async function logout() {
  await axios.get(`${import.meta.env.VITE_BACKEND_API_URL}/auth/logout`, {
    withCredentials: true,
  })
  await persistenceService.removeLocalConfig("accessToken")
  await persistenceService.removeLocalConfig("refreshToken")
  await persistenceService.removeLocalConfig("login_state")
}

async function signInUserWithGithubFB() {
  window.location.href = `${import.meta.env.VITE_BACKEND_API_URL}/auth/github`
}

async function signInUserWithGoogleFB() {
  window.location.href = `${import.meta.env.VITE_BACKEND_API_URL}/auth/google`
}

async function signInUserWithMicrosoftFB() {
  window.location.href = `${
    import.meta.env.VITE_BACKEND_API_URL
  }/auth/microsoft`
}

async function getInitialUserDetails() {
  const res = await axios.post<{
    data?: {
      me?: {
        uid: string
        displayName: string
        email: string
        photoURL: string
        isAdmin: boolean
        createdOn: string
        // emailVerified: boolean
      }
    }
    errors?: Array<{
      message: string
    }>
  }>(
    `${import.meta.env.VITE_BACKEND_GQL_URL}`,
    {
      query: `query Me {
      me {
        uid
        displayName
        email
        photoURL
        isAdmin
        createdOn
      }
    }`,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }
  )

  return res.data
}

const isGettingInitialUser: Ref<null | boolean> = ref(null)

async function setUser(user: HoppUser | null) {
  // 统一使用 accessToken 作为键名
  const storedAccessToken = await persistenceService.getLocalConfig(
    "accessToken"
  )

  // 如果有用户信息，尝试添加存储的 token；如果没有用户信息，直接设置为 null
  const userWithToken = user
    ? {
        ...user,
        // 优先使用存储的 token，如果没有则使用用户对象中的 token
        accessToken: storedAccessToken || user.accessToken,
      }
    : null

  currentUser$.next(userWithToken)
  probableUser$.next(userWithToken)

  await persistenceService.setLocalConfig(
    "login_state",
    JSON.stringify(userWithToken)
  )
}

async function setInitialUser() {
  isGettingInitialUser.value = true
  const res = await getInitialUserDetails()

  const error = res.errors && res.errors[0]

  // no cookies sent. so the user is not logged in
  if (error && error.message === "auth/cookies_not_found") {
    await setUser(null)
    isGettingInitialUser.value = false
    return
  }

  if (error && error.message === "user/not_found") {
    await setUser(null)
    isGettingInitialUser.value = false
    return
  }

  // cookies sent, but it is expired, we need to refresh the token
  if (error && error.message === "Unauthorized") {
    const isRefreshSuccess = await refreshToken()

    if (isRefreshSuccess) {
      setInitialUser()
    } else {
      await setUser(null)
      isGettingInitialUser.value = false
      await logout()
    }

    return
  }

  // no errors, we have a valid user
  if (res.data && res.data.me) {
    const hoppBackendUser = res.data.me

    const hoppUser: HoppUser = {
      uid: hoppBackendUser.uid,
      displayName: hoppBackendUser.displayName,
      email: hoppBackendUser.email,
      photoURL: hoppBackendUser.photoURL,
      // all our signin methods currently guarantees the email is verified
      emailVerified: true,
      accessToken: currentUser$.value?.accessToken,
    }

    await setUser(hoppUser)

    isGettingInitialUser.value = false

    authEvents$.next({
      event: "login",
      user: hoppUser,
    })

    return
  }
}

async function refreshToken() {
  try {
    const res = await axios.get(
      `${import.meta.env.VITE_BACKEND_API_URL}/auth/refresh`,
      {
        withCredentials: true,
      }
    )
    const isSuccessful = res.status === 200

    if (isSuccessful) {
      authEvents$.next({
        event: "token_refresh",
      })
    }

    return isSuccessful
  } catch (error) {
    return false
  }
}

async function sendMagicLink(email: string) {
  const res = await axios.post(
    `${import.meta.env.VITE_BACKEND_API_URL}/auth/signin`,
    {
      email,
    },
    {
      withCredentials: true,
    }
  )

  if (res.data && res.data.deviceIdentifier) {
    await persistenceService.setLocalConfig(
      "deviceIdentifier",
      res.data.deviceIdentifier
    )
  } else {
    throw new Error("test: does not get device identifier")
  }

  return res.data
}

async function handleOAuthCodeCallback() {
  // 检查URL中是否有IAM code回调
  const params = new URLSearchParams(window.location.search)
  const code = params.get("code")
  const state = params.get("state")
  const token = params.get("token")
  const teamId = params.get("teamId")

  if (token) {
    try {
      await persistenceService.removeLocalConfig("login_state")
      await persistenceService.removeLocalConfig("accessToken")
      await persistenceService.removeLocalConfig("refreshToken")
      await persistenceService.setLocalConfig("accessToken", token)
      await persistenceService.setLocalConfig("refreshToken", token)

      if (teamId) {
        try {
          const userDetails = await getInitialUserDetails()

          if (userDetails.data?.me) {
            const user: HoppUser = {
              uid: userDetails.data.me.uid,
              displayName: userDetails.data.me.displayName,
              email: userDetails.data.me.email,
              photoURL: userDetails.data.me.photoURL,
              accessToken: token,
              emailVerified: true,
            }

            currentUser$.next(user)

            const workspaceService = getService(WorkspaceService)

            const teamResult = await runGQLQuery<
              GetTeamQuery,
              GetTeamQueryVariables,
              ""
            >({
              query: GetTeamDocument,
              variables: { teamID: teamId },
            })

            if (E.isRight(teamResult) && teamResult.right.team) {
              const team = teamResult.right.team

              const userRole =
                team.teamMembers.find(
                  (member: any) => member.user.uid === user.uid
                )?.role || TeamAccessRole.Viewer

              await persistenceService.setLocalConfig(
                "REMEMBERED_TEAM_ID",
                team.id
              )

              workspaceService.changeWorkspace({
                teamID: team.id,
                teamName: team.name,
                type: "team",
                role: userRole,
              })
              return
            }
          } else {
            console.error("获取用户信息失败")
          }
        } catch (error) {
          console.error("获取团队信息失败", error)
        }
      }

      window.location.href = `${window.location.origin}${window.location.pathname}`
      return
    } catch (error) {
      console.log("token 写入失败", error)
    }
  }

  if (code && state) {
    try {
      // 调用IAM后端换取token和用户信息
      const res = await axios.post(
        `${import.meta.env.VITE_BACKEND_API_URL}/auth/iam/token`,
        { code, redirect_uri: window.location.origin }
      )

      const { access_token, refresh_token } = res.data
      if (access_token) {
        await persistenceService.setLocalConfig("accessToken", access_token)
        await persistenceService.setLocalConfig("refreshToken", refresh_token)
      }
      // 清理URL，避免code泄露
      window.location.href = "/"
      return
    } catch (e) {
      console.error("IAM换取token失败", e)
    }
    return
  }
}

export const def: AuthPlatformDef = {
  getCurrentUserStream: () => currentUser$,
  getAuthEventsStream: () => authEvents$ as any, // 类型转换以兼容扩展的事件类型
  getProbableUserStream: () => probableUser$,

  getCurrentUser: () => currentUser$.value,
  getProbableUser: () => probableUser$.value,

  getBackendHeaders() {
    const accessToken = currentUser$.value?.accessToken

    // console.log("getBackendHeaders - currentUser:", currentUser$.value)
    // console.log("getBackendHeaders - accessToken:", accessToken)

    return accessToken
      ? {
          Authorization: `Bearer ${accessToken}`,
        }
      : ({} as Record<string, string>)
  },
  getGQLClientOptions() {
    return {
      fetchOptions: {
        credentials: "include",
      },
    }
  },

  axiosPlatformConfig() {
    return {
      // for including cookies in the request
      withCredentials: true,
    }
  },

  /**
   * it is not possible for us to know if the current cookie is expired because we cannot access http-only cookies from js
   * hence just returning if the currentUser$ has a value associated with it
   */
  willBackendHaveAuthError() {
    return !currentUser$.value
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBackendGQLClientShouldReconnect(func) {
    authEvents$.subscribe((event) => {
      if (
        event.event == "login" ||
        event.event == "logout" ||
        event.event == "token_refresh"
      ) {
        func()
      }
    })
  },

  /**
   * we cannot access our auth cookies from javascript, so leaving this as null
   */
  getDevOptsBackendIDToken() {
    return null
  },
  async performAuthInit() {
    await handleOAuthCodeCallback()
    const probableUser = JSON.parse(
      (await persistenceService.getLocalConfig("login_state")) ?? "null"
    )
    probableUser$.next(probableUser)

    // 检查是否有自定义 IAM token
    const accessToken = await persistenceService.getLocalConfig("accessToken")

    if (accessToken && probableUser) {
      // 如果有 token 和用户信息，直接恢复用户状态
      console.log("恢复自定义 IAM 用户状态:", probableUser)
      currentUser$.next(probableUser)
      authEvents$.next({
        event: "login",
        user: probableUser,
      })
    } else {
      // 否则使用传统的 cookie 验证方式
      await setInitialUser()
    }
  },

  waitProbableLoginToConfirm() {
    return new Promise<void>((resolve, reject) => {
      if (this.getCurrentUser()) {
        resolve()
      }

      if (!probableUser$.value) reject(new Error("no_probable_user"))

      const unwatch = watch(isGettingInitialUser, (val) => {
        if (val === true || val === false) {
          resolve()
          unwatch()
        }
      })
    })
  },

  async signInWithEmail(email: string) {
    await sendMagicLink(email)
  },

  isSignInWithEmailLink(url: string) {
    const urlObject = new URL(url)
    const searchParams = new URLSearchParams(urlObject.search)

    return !!searchParams.get("token")
  },

  async verifyEmailAddress() {
    return
  },
  async signInUserWithGoogle() {
    await signInUserWithGoogleFB()
  },
  async signInUserWithGithub() {
    await signInUserWithGithubFB()
    return undefined
  },
  async signInUserWithMicrosoft() {
    await signInUserWithMicrosoftFB()
  },
  async signInWithEmailLink(email: string, url: string) {
    const urlObject = new URL(url)
    const searchParams = new URLSearchParams(urlObject.search)

    const token = searchParams.get("token")

    const deviceIdentifier = await persistenceService.getLocalConfig(
      "deviceIdentifier"
    )

    await axios.post(
      `${import.meta.env.VITE_BACKEND_API_URL}/auth/verify`,
      {
        token: token,
        deviceIdentifier,
      },
      {
        withCredentials: true,
      }
    )
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async setEmailAddress(_email: string) {
    return
  },

  async setDisplayName(name: string) {
    if (!name) return E.left("USER_NAME_CANNOT_BE_EMPTY")
    if (!currentUser$.value) return E.left("NO_USER_LOGGED_IN")

    const res = await updateUserDisplayName(name)

    if (E.isRight(res)) {
      await setUser({
        ...currentUser$.value,
        displayName: res.right.updateDisplayName.displayName ?? null,
      })

      return E.right(undefined)
    }
    return E.left(res.left)
  },

  async signOutUser() {
    // if (!currentUser$.value) throw new Error("No user has logged in")

    await logout()

    probableUser$.next(null)
    currentUser$.next(null)

    await persistenceService.removeLocalConfig("login_state")

    authEvents$.next({
      event: "logout",
    })
  },

  async processMagicLink() {
    if (this.isSignInWithEmailLink(window.location.href)) {
      const deviceIdentifier = await persistenceService.getLocalConfig(
        "deviceIdentifier"
      )

      if (!deviceIdentifier) {
        throw new Error(
          "Device Identifier not found, you can only signin from the browser you generated the magic link"
        )
      }

      await this.signInWithEmailLink(deviceIdentifier, window.location.href)

      await persistenceService.removeLocalConfig("deviceIdentifier")
      window.location.href = "/"
    }
  },
  getAllowedAuthProviders,
}
